// app/components/news/Hero.tsx
"use client";
import * as React from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Box, Grid } from "@mui/material";
import { useTranslations, useLocale } from "next-intl";
import { usePathname } from "next/navigation";

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string;
};

export default function Hero() {
  const t = useTranslations("News");
  const locale = useLocale();
  const pathname = usePathname() || "/";

  // path สำหรับเก็บใน CMS (ตัด prefix /en /zh)
  const pagePath = React.useMemo(() => {
    const parts = pathname.split("/");
    if (parts[1] === "en" || parts[1] === "zh") {
      const tail = parts.slice(2).join("/");
      return tail ? `/${tail}` : "/";
    }
    return pathname || "/";
  }, [pathname]);

  // ---- default text จาก i18n (ดีต่อ SEO / SSR) ----
  const defaultTitle = t("Hero.title");

  // ---- state ที่ใช้แสดงจริง (จะถูก override ด้วย inline edits ถ้ามี) ----
  const [title, setTitle] = React.useState(defaultTitle);

  // locale เปลี่ยน → reset ค่า default ก่อน
  React.useEffect(() => {
    setTitle(defaultTitle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  //
  React.useEffect(() => {
    const fetchInline = async () => {
      try {
        const res = await fetch(
          `/api/admin/page-inline?websiteId=${process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID}&path=${encodeURIComponent(
            pagePath
          )}&locale=${locale}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;
        const map: Record<string, string> = {};

        list.forEach((e) => {
          if (e.cmsKey) {
            map[e.cmsKey] = e.value;
            return;
          }
          const match = e.selector?.match(/data-cms-key="([^"]+)"/);
          if (match) {
            map[match[1]] = e.value;
          }
        });

        // override เฉพาะ key ที่เรากำหนด
        if (map["news.hero.title"]) setTitle(map["news.hero.title"]);
      } catch (err) {
        console.warn("[News.Hero] inline fetch error:", err);
      }
    };

    fetchInline();
  }, [locale, pagePath]);

  return (
    <Box className="news-hero">
      <Container className="container">
        <Grid container>
          <Grid size={12}>
            <Typography
              variant="h1"
              data-aos="zoom-in"
              data-cms-key="news.hero.title"
              sx={{
                pt: { xs: 7, md: 15, lg: 15 },
                whiteSpace: { xs: "normal", lg: "pre-line" }
              }}>
              {title}
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}